import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// P3-XSS-hardening (2026-07-10) — access_token / refresh_token NIE są już
// przechowywane w localStorage. Backend ustawia httpOnly cookies których JS
// nie może odczytać ani wykraść przez XSS. USER_KEY nadal jest w localStorage
// bo to nie-sekret (email, rola, imię) i pozwala na natychmiastowy render
// dashboardu przy odświeżeniu strony (bez czekania na /auth/me).
export const USER_KEY = "tt_user";

// Legacy keys — usuwamy przy pierwszym uruchomieniu nowej wersji. Po tygodniu
// można usunąć te linijki.
export const ACCESS_KEY = "tt_access";
export const REFRESH_KEY = "tt_refresh";
try {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
} catch (err) { /* localStorage disabled */ }

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 20000,
  // P3-XSS-hardening (2026-07-10) — httpOnly cookies protecting auth tokens.
  // `withCredentials: true` sprawia, że axios wysyła cookies przy same-origin
  // requestach (i cross-origin gdy backend ma `allow_credentials=true`).
  withCredentials: true,
});

export function setTokens({ user }) {
  // P3-XSS-hardening (2026-07-10) — tokeny ustawia backend jako httpOnly cookies.
  // Frontend zapisuje tylko user info (nie-secret) dla natychmiastowego renderu.
  if (user) {
    try { localStorage.setItem(USER_KEY, JSON.stringify(user)); }
    catch (err) { console.warn("[auth] localStorage user failed:", err); }
  }
}

export function getAccess() {
  // P3-XSS-hardening (2026-07-10) — cookies są httpOnly, JS ich NIE widzi.
  // Zwracamy null zawsze — logika interceptor'a nie dodaje Bearer.
  return null;
}
export function getRefresh() {
  return null;
}
export function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || "null");
  } catch {
    return null;
  }
}
export function clearAuth() {
  try { localStorage.removeItem(USER_KEY); } catch (err) { /* ignore */ }
}

// P3-XSS-hardening (2026-07-10) — request interceptor już NIE dodaje Bearer.
// Backend czyta access token z httpOnly cookie `tt_access` (auth_deps.py).
// Zostawiam pustą funkcję żeby w przyszłości łatwo dodać np. request-id header.
api.interceptors.request.use((config) => config);

let refreshing = null;

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    // Normalizuj Pydantic validation errors (lista obiektów) → czytelny string.
    const d = err.response?.data?.detail;
    if (Array.isArray(d)) {
      const msgs = d.map((it) => {
        if (typeof it === "string") return it;
        const loc = Array.isArray(it?.loc) ? it.loc.filter((x) => x !== "body").join(".") : "";
        const msg = it?.msg || "błąd walidacji";
        return loc ? `${loc}: ${msg}` : msg;
      });
      err.response.data.detail = msgs.join("; ");
    } else if (d && typeof d === "object") {
      err.response.data.detail = d.msg || JSON.stringify(d);
    }

    const original = err.config || {};
    // P3-hotfix (2026-07-10) — infinite reload loop protection:
    //  1) `_skipAuthRedirect: true` w request config → nigdy nie robimy redirect
    //     (używane przez bootstrap /auth/me — bo tam 401 jest oczekiwany dla wylogowanego)
    //  2) Nie przekierowujemy jeśli już jesteśmy na /login (zapobiega reload loop)
    //  3) Nie retry'ujemy /auth/refresh ani /auth/login (były przed hotfixem, zostawione)
    const skipRedirect = !!original._skipAuthRedirect;
    const alreadyOnLogin = typeof window !== "undefined" && window.location.pathname === "/login";

    if (err.response?.status === 401 && !original._retry && original.url !== "/auth/refresh" && original.url !== "/auth/login") {
      // Jeśli bootstrap oznaczył request jako "skip redirect", nawet nie próbujemy refresh.
      // Po prostu zwracamy błąd — komponent bootstrap sam ustawi user=null.
      if (skipRedirect) {
        return Promise.reject(err);
      }
      original._retry = true;
      try {
        if (!refreshing) {
          // P3-XSS-hardening (2026-07-10) — refresh z EMPTY BODY, refresh token
          // odczytany przez backend z httpOnly cookie `tt_refresh`.
          refreshing = axios
            .post(`${API_BASE}/auth/refresh`, {}, { withCredentials: true })
            .then((res) => {
              // Backend zwraca też tokeny w body dla back-compat (mobile/testy),
              // ale my ich NIE zapisujemy — cookie już zostało ustawione.
              if (res.data?.user) {
                try { localStorage.setItem(USER_KEY, JSON.stringify(res.data.user)); }
                catch (err) { console.warn("[auth] refresh user cache failed:", err); }
              }
              return true;
            })
            .finally(() => {
              refreshing = null;
            });
        }
        await refreshing;
        // Retry original request — cookies się dokleją automatycznie.
        return api(original);
      } catch (e) {
        clearAuth();
        // P3-hotfix: NIE przekierowujemy jeśli już jesteśmy na /login (loop protection)
        if (typeof window !== "undefined" && !alreadyOnLogin) {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(err);
  }
);

export function fmtPLN(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return new Intl.NumberFormat("pl-PL", { style: "currency", currency: "PLN", minimumFractionDigits: 2 }).format(Number(n));
}

export function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("pl-PL", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return s;
  }
}

export function fmtDateOnly(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("pl-PL", { year: "numeric", month: "2-digit", day: "2-digit" });
  } catch {
    return s;
  }
}

export const ROLE_LABELS = {
  owner: "Właściciel",
  manager: "Manager",
  waitress: "Kelnerka",
  bartender: "Barmanka",
  receptionist: "Recepcja masażu",
  cook: "Kucharz",
  warehouse: "Magazyn / Produkcja",
  accounting: "Księgowość",
  readonly: "Tylko podgląd",
};

export const PAYMENT_METHOD_LABELS = {
  cash: "Gotówka",
  card: "Karta",
  transfer: "Przelew",
  bank_transfer: "Przelew",
  owner_private: "Prywatnie właściciel",
  company_card: "Karta firmowa",
  compensation: "Kompensata",
  voucher: "Voucher",
  other: "Inne",
};

export const STATUS_LABELS = {
  draft: "Robocze",
  submitted: "Wysłane",
  approved: "Zatwierdzone",
  rejected: "Odrzucone",
  corrected: "Korekta",
  active: "Aktywny",
  sold: "Sprzedany",
  partially_used: "Częściowo użyty",
  used: "Wykorzystany",
  cancelled: "Anulowany",
  expired: "Przeterminowany",
  open: "Otwarty",
  acknowledged: "Potwierdzony",
  closed: "Zamknięty",
};

export function paymentLabel(m) { return PAYMENT_METHOD_LABELS[m] || m; }
export function statusLabel(s) { return STATUS_LABELS[s] || s; }

// Auth-aware URL for <img> tag. P3-XSS-hardening (2026-07-10): cookies są
// httpOnly i wysyłane automatycznie przez <img> dla same-origin. Backend
// akceptuje też ?auth=... query jako fallback (dla back-compat gdyby ktoś
// deep-linkował URL), ale my go już nie generujemy.
export function attachmentUrl(attachmentId) {
  return `${API_BASE}/attachments/${attachmentId}/download`;
}
